import {
  useFeatureFlagEnabled,
  useFeatureFlagPayload,
  useFeatureFlagVariantKey,
} from 'posthog-js/react'
import { useEffect, useState, useSyncExternalStore } from 'react'
import {
  freezeStickyFlag,
  getStickyFlag,
  noopSubscribeStickyFlags,
  readNoStickyFlag,
  subscribeStickyFlags,
} from '~/src/lib/stickyFeatureFlag'

const isDev = process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production'

interface UseIsFeatureOnOptions {
  /** Force the feature on in dev environments. Pass a string to also set the variant. */
  localOverride?: boolean | string
  /**
   * Force the feature on when the current hostname matches.
   * Accepts a single hostname or an array (e.g. `"v2.dupe.com"` or `["v2.dupe.com", "staging.dupe.com"]`).
   * Exact hostname match — subdomains must be listed explicitly.
   */
  hostOverride?: string | string[]
  /**
   * Freeze the first DEFINED value PostHog returns for this flag, for the rest
   * of the document. Opt-in, per call site.
   *
   * PostHog buckets on a hash of `distinct_id`, so a shopper who reads a flag
   * while ANONYMOUS and is `identify()`d moments later can be re-bucketed into a
   * different arm — the same person, two answers. In prod that hit ~10% of
   * registrations and asked 690 shoppers to subscribe twice.
   *
   * OUR CONFIG IS NOT THE PROBLEM: `ensure_experience_continuity` is enabled on
   * the flag and is the setting meant to prevent this. It doesn't hold — an open
   * posthog-js bug, https://github.com/PostHog/posthog-js/issues/2623. See
   * `~/src/lib/stickyFeatureFlag` for the full write-up.
   *
   * Use for experiments whose arm drives IRREVERSIBLE, cross-surface UX — the
   * test being "would asking twice be wrong?", e.g. "have we already asked this
   * shopper to subscribe?".
   *
   * ⛔ NOT for kill switches, rollout gates, or anything you may need to turn
   * off mid-session: a sticky flag ignores every later PostHog value until the
   * next document load.
   *
   * Overrides are unaffected and take precedence — `?flag=`, `localOverride` and
   * `hostOverride` are re-evaluated every render and are never frozen, so a QA
   * URL can't poison the store for the rest of the session.
   */
  sticky?: boolean
}

const useIsFeatureOn = (
  feature: string,
  options: UseIsFeatureOnOptions = {}
) => {
  const { localOverride, hostOverride, sticky = false } = options
  // ?flag= QA override, read from window at mount instead of useSearchParams():
  // this hook is in the always-mounted provider tree (SearchProvider,
  // ExtensionModalProvider), and useSearchParams() in that position forces a
  // CSR bailout for every statically rendered App Router page. The override
  // only ever applied post-hydration anyway (SSR has no search params), so a
  // mount-time read is observably identical on hard loads; ?flag= added via
  // soft navigation no longer toggles it (QA-only edge case, accepted).
  const [flagParam, setFlagParam] = useState<string | null>(null)
  useEffect(() => {
    setFlagParam(new URLSearchParams(window.location.search).get('flag'))
  }, [])
  const featureOn = useFeatureFlagEnabled(feature)
  // Kept unwrapped as well as wrapped: `|| {}` mints a new object every render,
  // which would make the freeze effect below re-run forever.
  const rawPayload = useFeatureFlagPayload(feature)
  const payload = (rawPayload || {}) as Record<string, any>
  const variant = useFeatureFlagVariantKey(feature)

  // Sticky read. `useSyncExternalStore` rather than reading the module Map
  // inline: it gives a SYNCHRONOUS first-render read (so a surface mounting
  // LATER — onboarding, seconds after the sign-up screen — never flickers
  // through a re-bucketed value), it is a read React's compiler can model
  // (`experimental.reactCompiler` is on), and `getServerSnapshot` pins both the
  // server render and the client's HYDRATION render to `undefined`, which makes
  // the DUP-1766 hydration mismatch impossible rather than merely unlikely.
  const stickySnapshot = useSyncExternalStore(
    sticky ? subscribeStickyFlags : noopSubscribeStickyFlags,
    sticky ? () => getStickyFlag(feature) : readNoStickyFlag,
    readNoStickyFlag
  )

  // Freeze in an EFFECT, never during render: a render-phase write to module
  // state is a side effect the compiler doesn't model, and a discarded
  // concurrent render could poison the store with a value that never committed.
  // It costs nothing here — the frozen value only has to be visible to a
  // LATER-mounting surface, and this fires on the commit where the value first
  // became defined.
  //
  // `undefined` is never frozen. An absent or DRAFT flag returns `undefined`
  // forever rather than `false`; latching that would turn a temporary absence
  // into a permanent one, which is the outage class documented in
  // `AuthContent`'s note about the brand flow going down for 100% of shoppers.
  useEffect(() => {
    if (!sticky) return
    if (featureOn === undefined || variant === undefined) return
    freezeStickyFlag(feature, { featureOn, variant, payload: rawPayload })
  }, [sticky, feature, featureOn, variant, rawPayload])

  // Only consider query param override if it exactly matches the feature name
  const queryParamOverride = flagParam === feature ? true : undefined

  // Only consider local override in dev if it's explicitly defined
  const devOverride =
    isDev && localOverride !== undefined ? localOverride : undefined

  const devOverrideVariant =
    devOverride && typeof devOverride === 'string' ? devOverride : undefined

  // Host override: treat as enabled when the current hostname is in the allow-list
  const hostOverrideValue: true | undefined = (() => {
    if (hostOverride === undefined || typeof window === 'undefined')
      return undefined
    const hostname = window.location.hostname
    const hosts = Array.isArray(hostOverride) ? hostOverride : [hostOverride]
    return hosts.includes(hostname) ? true : undefined
  })()

  // Priority: query param > localOverride (in dev) > hostOverride > PostHog
  const override =
    queryParamOverride !== undefined
      ? queryParamOverride
      : devOverride !== undefined
        ? devOverride
        : hostOverrideValue

  // A frozen value beats the live PostHog read; every override still beats the
  // frozen value. `??` and never `||`: a frozen `false` is a real answer — a
  // boolean flag that is off, or `variant === false` on a non-multivariate flag
  // — and `||` would drop stickiness for every off-cohort shopper.
  const effectiveFeatureOn = stickySnapshot?.featureOn ?? featureOn
  const effectiveVariant = stickySnapshot?.variant ?? variant
  const effectivePayload = stickySnapshot
    ? ((stickySnapshot.payload || {}) as Record<string, any>)
    : payload

  const isReady = override !== undefined || effectiveFeatureOn !== undefined

  const resolvedVariant = devOverrideVariant ?? effectiveVariant

  // `isTest` is the experiment-semantics derived flag callers should use
  // when a flag is wired up to a PostHog Experiment (variants like
  // `control` / `test`). It must work correctly whether the flag is
  // still boolean (pre-experiment-setup) or already multivariate, AND
  // it must respect the existing override priority chain (URL > dev >
  // host > PostHog) — a forced-on QA URL should still land in the test
  // arm even when PostHog has assigned the user to control.
  //
  // Resolution order:
  //   1. Explicit override → wins (boolean true / string 'test' = test;
  //      string 'control' = control)
  //   2. Multivariate variant assignment → variant === 'test' is truth
  //   3. Boolean flag (variant is true/false/undefined — `useFeatureFlagVariantKey`
  //      returns the boolean value for non-multivariate flags) → fall
  //      back to `featureOn` so on-cohort users stay in the test arm
  //      across the boolean → multivariate conversion window.
  const isTest = (() => {
    if (override === 'control') return false
    if (override === true || override === 'test') return true
    if (resolvedVariant === 'control') return false
    if (resolvedVariant === 'test') return true
    // Boolean flag (or loading) — fall back to the on/off boolean.
    return effectiveFeatureOn === true
  })()

  return {
    featureOn: override ?? effectiveFeatureOn,
    variant: resolvedVariant,
    isTest,
    payload: effectivePayload,
    isReady,
    loading: !isReady,
  } as const
}

export default useIsFeatureOn
